import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import * as s from '@/components/legal-styles'

export const Route = createFileRoute('/legal/terms')({
  component: TermsPage,
})

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 14, lineHeight: 1.7,
}
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px', borderBottom: '2px solid #EDE9E4',
  fontWeight: 700, color: '#1a1216', fontSize: 13,
}
const tdStyle: React.CSSProperties = {
  padding: '10px 14px', borderBottom: '1px solid #EDE9E4', color: '#4a4745', fontSize: 14,
}

function TermsPage() {
  return (
    <div>
      <Helmet>
        <title>Terms of Service | One-Email</title>
        <meta name="description" content="One-Email Terms of Service. Rules for using the platform, billing, acceptable use, liability, and termination." />
        <link rel="canonical" href="https://one-email.com/legal/terms" />
      </Helmet>

      <h1 style={s.heading}>Terms of Service</h1>
      <p style={s.updated}>Last updated: March 24, 2026</p>

      <div style={s.highlight}>
        <strong>Summary:</strong> These are the rules for using One-Email. You can send transactional and consent-based emails. Don&apos;t send spam. Don&apos;t abuse the platform. We&apos;ll do our best to keep your emails delivered. You&apos;re responsible for your content and your recipients&apos; consent.
      </div>

      {/* 1. Agreement to Terms */}
      <h2 id="agreement" style={s.h2}>1. Agreement to Terms</h2>
      <p style={s.p}>
        By creating an account or using One-Email (&ldquo;the Service&rdquo;), you agree to these Terms of Service, our{' '}
        <a href="/legal/acceptable-use">Acceptable Use Policy</a>, our{' '}
        <a href="/legal/privacy">Privacy Policy</a>, and, where applicable, our{' '}
        <a href="/legal/dpa">Data Processing Agreement</a> (collectively, &ldquo;the Agreement&rdquo;). If you are accepting on behalf of a company or organization, you represent that you have authority to bind that entity.
      </p>
      <p style={s.p}>If you do not agree, do not use the Service.</p>

      {/* 2. Description of Service */}
      <h2 id="service" style={s.h2}>2. Description of Service</h2>
      <p style={s.p}>
        One-Email is an email infrastructure platform that provides an API, SMTP relay, SDKs, CLI tools, a dashboard, webhook delivery, analytics, and related services for sending transactional and marketing email.
      </p>
      <p style={s.p}>
        One-Email operates its own email delivery infrastructure. All email is sent through our custom SMTP engine running on dedicated servers in the European Union. We do not use Amazon SES, or any third-party email relay. We are not a wrapper around another provider&apos;s infrastructure &mdash; we own the full stack from API gateway to SMTP delivery.
      </p>
      <p style={s.p}>The Service includes:</p>
      <ul style={s.ul}>
        <li style={s.li}><strong>API access</strong> for sending individual and batch emails</li>
        <li style={s.li}><strong>Dashboard</strong> for managing domains, API keys, analytics, and account settings</li>
        <li style={s.li}><strong>SDKs</strong> for TypeScript, Python, Go, and Rust</li>
        <li style={s.li}><strong>CLI tool</strong> for sending emails from the command line</li>
        <li style={s.li}><strong>Webhook delivery</strong> for real-time event notifications (delivery, bounce, complaint, open, click)</li>
        <li style={s.li}><strong>Suppression list management</strong> to prevent sending to invalid or opted-out addresses</li>
        <li style={s.li}><strong>DKIM signing</strong> for email authentication</li>
        <li style={s.li}><strong>IP warming</strong> for dedicated IP customers</li>
        <li style={s.li}><strong>RFC 8058 one-click unsubscribe</strong> for marketing email compliance</li>
      </ul>
      <p style={s.p}>The Service does <strong>not</strong> include:</p>
      <ul style={s.ul}>
        <li style={s.li}>Email list management or CRM functionality</li>
        <li style={s.li}>Email template design tools (you provide the HTML)</li>
        <li style={s.li}>Recipient consent management (you are responsible for obtaining consent)</li>
        <li style={s.li}>Inbox placement guarantees (delivery depends on recipient server policies and your sending reputation)</li>
      </ul>

      {/* 3. Account Eligibility and Registration */}
      <h2 id="eligibility" style={s.h2}>3. Account Eligibility and Registration</h2>
      <p style={s.p}>To use the Service, you must:</p>
      <ul style={s.ul}>
        <li style={s.li}>Be at least 18 years old or the age of majority in your jurisdiction</li>
        <li style={s.li}>Provide a valid email address and accurate account information</li>
        <li style={s.li}>Maintain one account per person or legal entity (unless explicitly approved)</li>
      </ul>
      <p style={s.p}>You are responsible for:</p>
      <ul style={s.ul}>
        <li style={s.li}>Maintaining the confidentiality of your account credentials and API keys</li>
        <li style={s.li}>All activity that occurs under your account</li>
        <li style={s.li}>Notifying us immediately at security@one-email.com if you suspect unauthorized access</li>
      </ul>
      <p style={s.p}>
        API keys are shown <strong>once</strong> at creation and cannot be retrieved afterward &mdash; we store only SHA-256 hashes. If a key is compromised, you must revoke it immediately via the dashboard or API. We are not liable for unauthorized usage resulting from your failure to secure your keys.
      </p>
      <p style={s.p}>
        API keys support two scopes: <code>send</code> (email endpoints only) and <code>full</code> (all endpoints). Use the minimum scope required for each use case. Test keys (<code>oe_test_sk_</code> prefix) route to sandbox mode and do not send real emails.
      </p>

      {/* 4. Acceptable Use */}
      <h2 id="acceptable-use" style={s.h2}>4. Acceptable Use</h2>
      <p style={s.p}>
        You agree to use the Service only for lawful purposes and in compliance with our{' '}
        <a href="/legal/acceptable-use">Acceptable Use Policy</a>, which is incorporated by reference and forms part of this Agreement. Violation of the AUP is a violation of these Terms.
      </p>
      <p style={s.p}>
        In summary: you must only send emails to recipients who have given consent to receive them. Purchased, scraped, rented, or shared email lists are prohibited. Phishing, malware, and deceptive content are prohibited. Full details are in the AUP.
      </p>
      <p style={s.p}>
        You are solely responsible for the content of the emails you send through the Service and for compliance with all applicable laws, including but not limited to the CAN-SPAM Act (US), CASL (Canada), GDPR (EU), the Spam Act 2003 (Australia), and the LGPD (Brazil). Under CAN-SPAM, One-Email performs &ldquo;routine conveyance&rdquo; &mdash; you are the sender and initiator, and you bear compliance responsibility.
      </p>

      {/* 5. Payment and Billing */}
      <h2 id="billing" style={s.h2}>5. Payment and Billing</h2>

      <h3 style={s.h3}>Transactional Email Tiers</h3>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Tier</th>
              <th style={thStyle}>Price</th>
              <th style={thStyle}>Emails/month</th>
              <th style={thStyle}>Daily Cap</th>
              <th style={thStyle}>Overage Rate</th>
              <th style={thStyle}>Domains</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>Free</td>
              <td style={tdStyle}>$0/mo</td>
              <td style={tdStyle}>10,000</td>
              <td style={tdStyle}>1,000/day</td>
              <td style={tdStyle}>No overages (hard-block)</td>
              <td style={tdStyle}>1</td>
            </tr>
            <tr>
              <td style={tdStyle}>Starter</td>
              <td style={tdStyle}>$5/mo</td>
              <td style={tdStyle}>25,000</td>
              <td style={tdStyle}>&mdash;</td>
              <td style={tdStyle}>$0.40/1K</td>
              <td style={tdStyle}>2</td>
            </tr>
            <tr>
              <td style={tdStyle}>Pro</td>
              <td style={tdStyle}>$18/mo</td>
              <td style={tdStyle}>75,000</td>
              <td style={tdStyle}>&mdash;</td>
              <td style={tdStyle}>$0.30/1K</td>
              <td style={tdStyle}>10</td>
            </tr>
            <tr>
              <td style={tdStyle}>Growth</td>
              <td style={tdStyle}>$45/mo</td>
              <td style={tdStyle}>300,000</td>
              <td style={tdStyle}>&mdash;</td>
              <td style={tdStyle}>$0.20/1K</td>
              <td style={tdStyle}>25</td>
            </tr>
            <tr>
              <td style={tdStyle}>Scale</td>
              <td style={tdStyle}>$349/mo</td>
              <td style={tdStyle}>2,000,000</td>
              <td style={tdStyle}>&mdash;</td>
              <td style={tdStyle}>$0.10/1K</td>
              <td style={tdStyle}>Unlimited</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 style={s.h3}>Marketing Email Tiers (separate, additive)</h3>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Tier</th>
              <th style={thStyle}>Price</th>
              <th style={thStyle}>Emails/month</th>
              <th style={thStyle}>Overage Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>Marketing Starter</td>
              <td style={tdStyle}>$15/mo</td>
              <td style={tdStyle}>10,000</td>
              <td style={tdStyle}>$1.50/1K</td>
            </tr>
            <tr>
              <td style={tdStyle}>Marketing Pro</td>
              <td style={tdStyle}>$59/mo</td>
              <td style={tdStyle}>50,000</td>
              <td style={tdStyle}>$1.00/1K</td>
            </tr>
            <tr>
              <td style={tdStyle}>Marketing Growth</td>
              <td style={tdStyle}>$149/mo</td>
              <td style={tdStyle}>200,000</td>
              <td style={tdStyle}>$0.75/1K</td>
            </tr>
            <tr>
              <td style={tdStyle}>Marketing Scale</td>
              <td style={tdStyle}>$399/mo</td>
              <td style={tdStyle}>500,000</td>
              <td style={tdStyle}>$0.50/1K</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 style={s.h3}>Add-ons</h3>
      <ul style={s.ul}>
        <li style={s.li}><strong>Dedicated IP:</strong> $29/mo per IP (available on Growth and Scale tiers only). Includes auto-warming, health monitoring, and overflow routing to shared IPs during warm-up.</li>
      </ul>

      <h3 style={s.h3}>Overage Policy</h3>
      <ul style={s.ul}>
        <li style={s.li}><strong>Free tier:</strong> Hard cap at 10,000/month and 1,000/day. No buffer, no overages. Sending is blocked at the limit.</li>
        <li style={s.li}><strong>Paid tiers:</strong> Each tier includes a <strong>10% free overage buffer</strong> above the included volume (e.g., Starter gets 2,500 free overage emails before charges apply).</li>
        <li style={s.li}><strong>First 3 overages:</strong> No charge. We&apos;ll send you a friendly email &mdash; &ldquo;this one&apos;s on us.&rdquo;</li>
        <li style={s.li}><strong>4th overage without acknowledgment:</strong> Sending is hard-blocked. Queued emails are <strong>held, not dropped</strong>. You can acknowledge the overage situation in the dashboard to resume sending, at which point normal overage rates apply going forward.</li>
        <li style={s.li}>You can upgrade your tier at any time to avoid overages.</li>
      </ul>

      <h3 style={s.h3}>General Billing Terms</h3>
      <ul style={s.ul}>
        <li style={s.li}>Subscription fees are billed monthly in advance. Overage charges are billed monthly in arrears.</li>
        <li style={s.li}>Annual billing is available at approximately 20% discount.</li>
        <li style={s.li}>All payment processing is handled by <a href="https://polar.sh" target="_blank" rel="noopener noreferrer">Polar</a>. By subscribing, you agree to Polar&apos;s terms of service.</li>
        <li style={s.li}>One-Email does not store credit card numbers, bank details, or payment tokens. We store only your tier, billing status, and Polar subscription ID.</li>
        <li style={s.li}><strong>Failed payment:</strong> 7-day grace period, then account suspension (sending paused, data retained), then termination after 30 additional days.</li>
        <li style={s.li}><strong>Refunds:</strong> No refunds for partial months on monthly plans. Annual plans receive a prorated refund for unused full months if cancelled during the term.</li>
        <li style={s.li}><strong>Price changes:</strong> We will provide at least 30 days written notice before any price increase takes effect for existing customers.</li>
      </ul>

      {/* 6. Service Availability */}
      <h2 id="availability" style={s.h2}>6. Service Availability</h2>
      <p style={s.p}>
        We target <strong>99.9% API uptime</strong> but do not guarantee uninterrupted service. For customers on paid plans, our{' '}
        <a href="/legal/sla">Service Level Agreement</a> defines uptime commitments and service credits.
      </p>
      <p style={s.p}>
        Email delivery is subject to recipient server acceptance. We do not guarantee inbox placement &mdash; delivery depends on the recipient&apos;s mail server policies, your domain reputation, your sending practices, and factors outside our control.
      </p>
      <p style={s.p}>
        Scheduled maintenance will be communicated in advance when possible. If our primary SMTP infrastructure is temporarily unavailable, emails remain in queue and are retried &mdash; we do not silently drop emails, and we do not fall back to any third-party sending service.
      </p>

      {/* 7. Data and Privacy */}
      <h2 id="data-privacy" style={s.h2}>7. Data and Privacy</h2>
      <p style={s.p}>
        Your use of the Service is governed by our <a href="/legal/privacy">Privacy Policy</a> and, where applicable, our <a href="/legal/dpa">Data Processing Agreement</a>.
      </p>
      <p style={s.p}><strong>What we store:</strong></p>
      <ul style={s.ul}>
        <li style={s.li}>Email metadata (sender, recipient, delivery status, timestamps, tags, sending IP, SMTP message ID). Subject lines are stored as SHA-256 hashes only &mdash; not human-readable.</li>
        <li style={s.li}>Metadata is retained per your tier: Free (3 days), Starter (7 days), Pro (30 days), Growth (90 days), Scale (1 year).</li>
      </ul>
      <p style={s.p}><strong>What we do NOT store:</strong></p>
      <ul style={s.ul}>
        <li style={s.li}>Email body content (HTML/plaintext) &mdash; processed in memory, discarded immediately after delivery.</li>
        <li style={s.li}>Custom headers &mdash; discarded after sending.</li>
        <li style={s.li}>Attachment content &mdash; stored temporarily during delivery (seconds to minutes), deleted after confirmation. Orphaned attachments purged daily.</li>
        <li style={s.li}>Credit card or payment details &mdash; handled entirely by Polar.</li>
      </ul>
      <p style={s.p}>
        You can request full data deletion at any time via <code>DELETE /v1/account/data</code> or by emailing privacy@one-email.com.
      </p>

      {/* 8. Intellectual Property */}
      <h2 id="ip" style={s.h2}>8. Intellectual Property</h2>
      <p style={s.p}>
        <strong>You own your content.</strong> All email content, templates, recipient lists, and data you provide remain your property. We claim no ownership.
      </p>
      <p style={s.p}>
        <strong>We own the platform.</strong> The API, dashboard, CLI, infrastructure, documentation, and all software constituting the Service are the intellectual property of One-Email and its parent company, IMP Capital.
      </p>
      <p style={s.p}>
        <strong>Limited license grant:</strong> You grant us a limited license to process your content solely for the purpose of providing the Service (delivering your emails). This license terminates when your account is closed.
      </p>
      <p style={s.p}>
        <strong>SDK and CLI license:</strong> The One-Email SDK and CLI are open source. You may use them freely under their respective open source licenses.
      </p>
      <p style={s.p}>
        <strong>Feedback:</strong> If you provide product feedback or suggestions, we may use them without obligation.
      </p>

      {/* 9. Termination */}
      <h2 id="termination" style={s.h2}>9. Termination</h2>

      <h3 style={s.h3}>By You</h3>
      <p style={s.p}>You can cancel your account at any time via the dashboard. On cancellation:</p>
      <ul style={s.ul}>
        <li style={s.li}>Your account remains active until the end of the current billing period.</li>
        <li style={s.li}>After the billing period ends, a <strong>30-day grace period</strong> begins &mdash; data is retained but sending is disabled. You can export your data during this period.</li>
        <li style={s.li}>After 30 days, all data is permanently deleted.</li>
      </ul>

      <h3 style={s.h3}>By Us (AUP Violations)</h3>
      <p style={s.p}>
        We may suspend or terminate accounts that violate the AUP. Our enforcement follows a graduated path: warning, suspension (sending paused, queued emails held), termination. For severe violations (phishing, malware, spam trap hits), we may act immediately without warning.
      </p>
      <p style={s.p}>On termination for AUP violation:</p>
      <ul style={s.ul}>
        <li style={s.li}>All queued emails are dropped.</li>
        <li style={s.li}>DKIM keys are revoked.</li>
        <li style={s.li}>The current billing period is refunded.</li>
      </ul>

      <h3 style={s.h3}>By Us (Other Reasons)</h3>
      <p style={s.p}>
        We may discontinue the Service with <strong>90 days notice</strong>. In this case, all customers receive prorated refunds for unused service.
      </p>

      {/* 10. Limitation of Liability */}
      <h2 id="liability" style={s.h2}>10. Limitation of Liability</h2>
      <p style={s.p}>To the maximum extent permitted by law:</p>
      <ul style={s.ul}>
        <li style={s.li}>Our total aggregate liability shall not exceed the fees you paid in the <strong>12 months preceding the claim</strong>.</li>
        <li style={s.li}>We are <strong>not liable</strong> for indirect, incidental, consequential, special, or punitive damages, including lost profits, lost data, or business interruption.</li>
        <li style={s.li}>We are <strong>not liable</strong> for email delivery failures caused by recipient server policies, DNS misconfiguration by you, or third-party service outages.</li>
        <li style={s.li}>We are <strong>not liable</strong> for damages resulting from compromised API keys that you failed to secure.</li>
        <li style={s.li}>We are <strong>not liable</strong> for the content of emails you send through the Service.</li>
      </ul>
      <p style={s.p}>
        <strong>Carve-outs:</strong> These limitations do not apply to liability arising from fraud, gross negligence, or willful misconduct, or where limitation is prohibited by applicable law (including mandatory Swedish consumer protection provisions).
      </p>

      {/* 11. Indemnification */}
      <h2 id="indemnification" style={s.h2}>11. Indemnification</h2>
      <p style={s.p}>
        You agree to indemnify, defend, and hold harmless One-Email, IMP Capital, and their officers, directors, and employees from any claims, damages, losses, or expenses (including reasonable legal fees) arising from:
      </p>
      <ul style={s.ul}>
        <li style={s.li}>Your use of the Service</li>
        <li style={s.li}>Your email content</li>
        <li style={s.li}>Your violation of these Terms or the AUP</li>
        <li style={s.li}>Your violation of applicable laws (including CAN-SPAM, CASL, GDPR, Spam Act 2003)</li>
        <li style={s.li}>Any third-party claim related to emails you sent through the Service</li>
      </ul>

      {/* 12. Governing Law and Disputes */}
      <h2 id="governing-law" style={s.h2}>12. Governing Law and Disputes</h2>
      <p style={s.p}>
        These Terms are governed by the laws of <strong>Sweden</strong>, without regard to conflict of law principles. Any disputes shall be resolved in the courts of Kalmar, Sweden.
      </p>
      <p style={s.p}>
        <strong>For EU/EEA customers:</strong> Nothing in these Terms limits your rights under mandatory EU consumer protection laws. Where EU or UK data protection law conflicts with these Terms, data protection law takes precedence.
      </p>
      <p style={s.p}>
        <strong>For Australian customers:</strong> Nothing in these Terms excludes, restricts, or modifies any rights under the Australian Consumer Law that cannot be excluded by agreement.
      </p>

      {/* 13. Changes to Terms */}
      <h2 id="changes" style={s.h2}>13. Changes to Terms</h2>
      <p style={s.p}>We may update these Terms from time to time. For material changes:</p>
      <ul style={s.ul}>
        <li style={s.li}>We will provide at least <strong>30 days notice</strong> via email and a dashboard banner.</li>
        <li style={s.li}>Continued use after the notice period constitutes acceptance.</li>
        <li style={s.li}>Previous versions will be archived at <code>/legal/terms/archive</code>.</li>
      </ul>
      <p style={s.p}>
        For material changes to pricing, billing, or the AUP, we may require explicit re-acknowledgment in the dashboard.
      </p>

      {/* 14. General Provisions */}
      <h2 id="general" style={s.h2}>14. General Provisions</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions remain in effect.</li>
        <li style={s.li}><strong>Entire agreement:</strong> These Terms, together with the AUP, Privacy Policy, DPA, and SLA, constitute the complete agreement.</li>
        <li style={s.li}><strong>Assignment:</strong> You may not transfer your account. We may assign the Agreement in connection with a merger, acquisition, or sale of assets.</li>
        <li style={s.li}><strong>Force majeure:</strong> Neither party is liable for failures caused by events beyond reasonable control (natural disasters, war, government action, internet outages, pandemic).</li>
        <li style={s.li}><strong>No waiver:</strong> Our failure to enforce a provision does not waive the right to enforce it later.</li>
      </ul>

      {/* 15. Contact */}
      <h2 id="contact" style={s.h2}>15. Contact</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>General:</strong> support@one-email.com</li>
        <li style={s.li}><strong>Security:</strong> security@one-email.com</li>
        <li style={s.li}><strong>Privacy:</strong> privacy@one-email.com</li>
        <li style={s.li}><strong>Abuse:</strong> abuse@one-email.com</li>
        <li style={s.li}><strong>Legal entity:</strong> IMP Capital AB, Rotebrunnsv&auml;gen 3, 393 55 Kalmar, Sweden</li>
      </ul>
    </div>
  )
}
